import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, Checkbox, Chip, Divider, FormControlLabel, IconButton, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { useTranslation } from 'react-i18next';

const BLOCK_ORDER = ['identity', 'hero', 'usps', 'services', 'about', 'tire_hotel', 'gallery', 'seo'];

function blockOf(path) {
  return path.split('.')[0];
}

function labelFor(t, path, content) {
  const parts = path.split('.');
  if (parts[0] === 'services') {
    const service = (content?.services || []).find((s) => s.key === parts[1]);
    return service ? `${service.name}` : path;
  }
  if (parts[0] === 'usps') return `${t('website.field.usp')} ${parts[1].replace('u', '')} (${t(`website.field.${parts[2]}`)})`;
  if (parts[0] === 'gallery') return t('website.field.caption');
  return t(`website.field.${parts.join('_')}`, { defaultValue: path });
}

/**
 * Level 3 editor: every authored field as a text field, grouped by block, with
 * "Kirjoita uudelleen" per field, section visibility and hidden reviews. Explicit Save and
 * Cancel; nothing is written on blur.
 */
export default function ContentEditor({
  content, fields, sections, focusField, onSave, onRegenerate, saving, regeneratingField, disabled,
}) {
  const { t } = useTranslation();
  const [values, setValues] = useState({});
  const [hiddenSections, setHiddenSections] = useState([]);
  const [hiddenReviews, setHiddenReviews] = useState([]);
  const [heroVariant, setHeroVariant] = useState('typographic');
  const refs = useRef({});

  useEffect(() => {
    const next = {};
    (fields || []).forEach((f) => { next[f.path] = f.value || ''; });
    setValues(next);
    setHiddenSections(content?.sections_hidden || []);
    setHiddenReviews(content?.reviews?.hidden_ids || []);
    setHeroVariant(content?.hero?.variant || 'typographic');
  }, [content, fields]);

  useEffect(() => {
    if (focusField && refs.current[focusField]) {
      refs.current[focusField].focus();
      refs.current[focusField].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [focusField]);

  const grouped = useMemo(() => {
    const groups = {};
    (fields || []).forEach((f) => {
      const block = blockOf(f.path);
      if (!groups[block]) groups[block] = [];
      groups[block].push(f);
    });
    return BLOCK_ORDER.filter((b) => groups[b]).map((b) => ({ block: b, items: groups[b] }));
  }, [fields]);

  const dirtyFields = useMemo(() => {
    const out = {};
    (fields || []).forEach((f) => {
      if ((values[f.path] ?? '') !== (f.value || '')) out[f.path] = values[f.path];
    });
    return out;
  }, [fields, values]);

  const sectionsDirty = JSON.stringify(hiddenSections) !== JSON.stringify(content?.sections_hidden || []);
  const reviewsDirty = JSON.stringify(hiddenReviews) !== JSON.stringify(content?.reviews?.hidden_ids || []);
  const heroDirty = heroVariant !== (content?.hero?.variant || 'typographic');
  const dirty = Object.keys(dirtyFields).length > 0 || sectionsDirty || reviewsDirty || heroDirty;

  const reset = () => {
    const next = {};
    (fields || []).forEach((f) => { next[f.path] = f.value || ''; });
    setValues(next);
    setHiddenSections(content?.sections_hidden || []);
    setHiddenReviews(content?.reviews?.hidden_ids || []);
    setHeroVariant(content?.hero?.variant || 'typographic');
  };

  const save = () => {
    const body = { fields: dirtyFields };
    if (sectionsDirty) body.sections_hidden = hiddenSections;
    if (reviewsDirty) body.hidden_review_ids = hiddenReviews;
    if (heroDirty) body.hero_variant = heroVariant;
    onSave(body);
  };

  const toggleSection = (key) => {
    setHiddenSections((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const reviews = content?.reviews?.items || [];

  return (
    <Box data-testid="website-editor">
      <Stack direction="row" spacing={1} sx={{ mb: 2, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1, py: 1 }}>
        <Button variant="contained" onClick={save} disabled={!dirty || saving || disabled} data-testid="editor-save">
          {t('website.save')}
        </Button>
        <Button onClick={reset} disabled={!dirty || saving} data-testid="editor-cancel">{t('website.cancel')}</Button>
      </Stack>

      {grouped.map(({ block, items }) => (
        <Box key={block} sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>{t(`website.block.${block}`, { defaultValue: block })}</Typography>
          {items.map((f) => {
            const multiline = f.max_length > 100;
            const length = (values[f.path] || '').length;
            return (
              <Box key={f.path} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 1.5 }}>
                <TextField
                  inputRef={(el) => { refs.current[f.path] = el; }}
                  label={labelFor(t, f.path, content)}
                  value={values[f.path] || ''}
                  onChange={(e) => setValues((v) => ({ ...v, [f.path]: e.target.value }))}
                  fullWidth
                  size="small"
                  multiline={multiline}
                  minRows={multiline ? 2 : 1}
                  inputProps={{ maxLength: f.max_length, 'data-testid': `field-${f.path}` }}
                  helperText={`${length}/${f.max_length}`}
                  disabled={disabled}
                />
                <Tooltip title={t('website.rewrite')}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => onRegenerate(f.path)}
                      disabled={disabled || Boolean(regeneratingField)}
                      data-testid={`rewrite-${f.path}`}
                    >
                      <AutorenewIcon fontSize="small" className={regeneratingField === f.path ? 'spin' : ''} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            );
          })}
        </Box>
      ))}

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('website.hero_variant')}</Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
        {['typographic', 'photo', 'split'].map((v) => (
          <Chip
            key={v}
            label={t(`website.hero_variant_${v}`)}
            color={heroVariant === v ? 'primary' : 'default'}
            onClick={() => setHeroVariant(v)}
            disabled={disabled}
            data-testid={`hero-variant-${v}`}
          />
        ))}
      </Stack>
      <Typography variant="caption" color="text.secondary">{t('website.hero_variant_help')}</Typography>

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('website.sections_title')}</Typography>
      {(sections || []).map((key) => (
        <FormControlLabel
          key={key}
          control={<Checkbox checked={!hiddenSections.includes(key)} onChange={() => toggleSection(key)} disabled={disabled} />}
          label={t(`website.block.${key}`, { defaultValue: key })}
          sx={{ display: 'flex' }}
        />
      ))}

      {reviews.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('website.reviews_title')}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{t('website.reviews_help')}</Typography>
          {reviews.map((r) => (
            <FormControlLabel
              key={r.id}
              control={(
                <Checkbox
                  checked={!hiddenReviews.includes(r.id)}
                  onChange={() => setHiddenReviews((prev) => (prev.includes(r.id) ? prev.filter((i) => i !== r.id) : [...prev, r.id]))}
                  disabled={disabled}
                />
              )}
              label={`${'★'.repeat(r.rating)} ${r.first_name || t('website.review_anonymous')}: ${(r.comment || '').slice(0, 80)}`}
              sx={{ display: 'flex', alignItems: 'flex-start' }}
            />
          ))}
        </>
      )}
    </Box>
  );
}
